import React, {useCallback, useMemo} from 'react'
import {MAIN_IMAGES_IMAGE_TYPE} from '../constants'
import {findImagesBy} from '../utils/image-groups-utils'
import {isServer} from '../utils/utils'
import useMultiSite from './use-multi-site'
import {categoryUrlBuilder} from '../utils/url'
import useOcapiSite from './useOcapiSite'
import {absoluteUrl} from '../utils/url'
import {useLocation} from 'react-router-dom'
import {getDisplayPrice, isProductSet} from '../utils/product-utils'
import {useIntl} from 'react-intl'

const validValuesForAvailabilityField = [
    'InStock',
    'LowStock',
    'OutOfStock',
    'LimitedAvailability',
    'OnlineOnly',
    'PreOrder',
    'SoldOut',
    'Discontinued'
]

const getAvailability = (product) => {
    if (isProductSet(product) && product.c_isOrderable) {
        return validValuesForAvailabilityField[0]
    }
    const productAvailability = product?.c_availability
    if (!productAvailability) {
        return null
    }
    return (
        validValuesForAvailabilityField.find(
            (value) => productAvailability?.toLowerCase() === value?.toLowerCase()
        ) || null
    )
}

const getProductImage = (product) => {
    const imageGroups = product?.imageGroups || []
    const selectedVariationAttributes = product?.variationValues || {}
    const productImagesArr = findImagesBy(
        imageGroups,
        product?.c_extraMainImage,
        product?.c_extraMainImage2,
        {
            viewType: MAIN_IMAGES_IMAGE_TYPE,
            selectedVariationAttributes
        }
    )
    return productImagesArr?.[0]?.link
}

const getBreadcrumbItem = (position, name, url) => {
    return {
        '@type': 'ListItem',
        position: position,
        name: name,
        item: absoluteUrl(url)
    }
}

const useStructuredData = (category, product) => {
    const context = 'https://www.schema.org'
    const {buildUrl} = useMultiSite()
    const {logo} = useOcapiSite()
    const {pathname} = useLocation()

    const getProductPageJSONLDScript = useCallback(
        (product, categoryName = 'thing') => {
            if (!product) {
                return null
            }
            const type = product?.['product-type']?.toLowerCase?.() || 'product'
            const id = product?.id
            const brand = product?.brand || 'Stokke'
            const name = product?.name
            const image = getProductImage(product)
            const description = product?.pageDescription
            const availability = getAvailability(product)
            const {displayPrice} = getDisplayPrice(product)
            const price = displayPrice?.toFixed?.(2)
            const priceCurrency = product?.currency
            const url = product?.c_canonicalUrl || product?.slugUrl || product?.c_slugUrl
            const dataObject = {
                '@context': context,
                '@type': type,
                sku: id,
                brand: {
                    '@type': 'Brand',
                    name: brand,
                    logo
                },
                category: categoryName,
                ...(name && {name}),
                ...(description && {description}),
                ...(image && {image}),
                offers: {
                    '@type': 'Offer',
                    ...(price && {price}),
                    ...(priceCurrency && {priceCurrency}),
                    ...(availability && {availability}),
                    ...(url && {url})
                }
            }
            return <script type="application/ld+json">{JSON.stringify(dataObject)}</script>
        },
        [logo]
    )
    const getBreadcrumbListJSONLDScript = useCallback(
        (primaryCategory, product) => {
            if (!primaryCategory) {
                return null
            }
            const host = !isServer && window?.location?.host
            const itemsArr = []
            const dataObject = {
                '@context': context,
                '@type': 'BreadcrumbList'
            }

            // Skip the first element in the array as it is the root
            // category(allproducts) and we don't want to display it
            for (let i = 1; i < primaryCategory?.parentCategoryTree?.length; i++) {
                const category = primaryCategory?.parentCategoryTree?.[i]
                const categoryUrl = buildUrl(categoryUrlBuilder(category))
                itemsArr.push(
                    getBreadcrumbItem(
                        i,
                        category?.name,
                        host ? `https://${host}${categoryUrl}` : `${categoryUrl}`
                    )
                )
            }

            // Add the product itself as the last element in the array
            if (product) {
                itemsArr.push(getBreadcrumbItem(itemsArr.length + 1, product?.name, pathname))
            }

            dataObject.itemListElement = itemsArr
            const resultString = itemsArr.length ? JSON.stringify(dataObject) : null
            return itemsArr.length ? (
                <script type="application/ld+json">{resultString}</script>
            ) : null
        },
        [buildUrl, pathname]
    )

    const productPageJSONLDScript = useMemo(
        () => getProductPageJSONLDScript(product, category?.name),
        [product, category?.name, getProductPageJSONLDScript]
    )
    const breadcrumbListJSONLDScript = useMemo(
        () => getBreadcrumbListJSONLDScript(category, product),
        [category, product, getBreadcrumbListJSONLDScript]
    )

    return {productPageJSONLDScript, breadcrumbListJSONLDScript}
}
export default useStructuredData

export const useHomePageStructuredData = () => {
    const {formatMessage} = useIntl()
    const homePageOrganizationJSONLDScript = useMemo(() => {
        const dataObject = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: formatMessage({defaultMessage: 'Stokke', id: 'head.company.name'}),
            legalName: 'Stokke AS',
            url: 'https://www.stokke.com/',
            description: formatMessage({
                defaultMessage:
                    'Stokke AS is a leading Norwegian design and innovation company dedicated to creating high-quality, ergonomic products for children and families.',
                id: 'head.company.description'
            }),
            address: {
                '@type': 'PostalAddress',
                streetAddress: 'Parkgata 6',
                addressLocality: 'Ålesund',
                postalCode: '6003',
                addressCountry: 'Norway'
            },
            identifier: {
                '@type': 'PropertyValue',
                propertyID: 'Registration Number',
                value: '970 983 325'
            },
            employee: {
                '@type': 'Person',
                name: 'Rob Webster',
                jobTitle: 'CEO'
            }
        }

        return <script type="application/ld+json">{JSON.stringify(dataObject)}</script>
    }, [formatMessage])

    const homePageWebSiteJSONLDScript = useMemo(() => {
        const dataObject = {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: formatMessage({defaultMessage: 'Stokke', id: 'head.company.name'}),
            url: 'https://www.stokke.com/',
            potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.stokke.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string'
            }
        }

        return <script type="application/ld+json">{JSON.stringify(dataObject)}</script>
    }, [formatMessage])

    return {homePageOrganizationJSONLDScript, homePageWebSiteJSONLDScript}
}
